const libPictView = require('pict-view');

// Offer the filter box once the connected list exceeds one page.
const PAGE_SIZE = 25;

/**
 * ChildListTab — a generic, config-driven read-view tab that MANAGES an entity's 1:N foreign-key children
 * (e.g. a Test Specification Set's Test Specifications, a Pay Item's Line Items).
 *
 * pict-section-recordset's read view has no native "child list" tab type, but its `View` tab type renders
 * any registered pict-view into the tab body. One configured instance of this view is registered per
 * (parent, child, FK) relationship (see PlatformConfiguration-Application-Web.js); the builder points a
 * `{ Type: 'View', View: <id> }` read tab at it (see MasterList-Builder.childListViewId).
 *
 * Unlike a many-to-many join (managed by RecordSet-AssociationEditor via a join table), a 1:N child is
 * linked by SETTING the child record's own foreign-key column. So "link" = PUT the child with
 * { <FilterField>: <parentId> }; "unlink" = PUT it with { <FilterField>: 0 }.
 *
 * The connected list is SERVER-PAGINATED (a set can have thousands of children): one page of PAGE_SIZE at a
 * time, with a server-side filter (LK on the display field) and Prev/Next. The `_state` is passed as the
 * render RECORD (not a shared AppData slot) so many child-list tabs on one record never collide; the paged
 * list region re-renders into its own container (`#PCFG-CL-List-<viewID>`) so the filter input keeps focus.
 *
 * Per-instance options: ParentRecordSet, ChildRecordSet, FilterField, BaseField (parent PK, default
 * ID<ParentRecordSet>), DisplayField, Columns ([{ Key, DisplayName }]), ChipFields, Title.
 */
class ChildListTab extends libPictView
{
	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	// The registered view id — inline handlers reach this exact instance via _Pict.views[viewID].
	get viewID()
	{
		return this.options.ViewIdentifier || this.Hash;
	}

	get childIDField() { return `ID${ this.options.ChildRecordSet }`; }
	get childGUIDField() { return `GUID${ this.options.ChildRecordSet }`; }
	get baseField() { return this.options.BaseField || `ID${ this.options.ParentRecordSet }`; }
	get displayField() { return this.options.DisplayField || 'Name'; }
	get noun() { return this.options.Title || this.options.ChildRecordSet; }

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)));
		});
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)));
		});
	}

	_parentID()
	{
		const tmpParent = this.pict.AppData[`${ this.options.ParentRecordSet }Details`] || {};
		return tmpParent[this.baseField];
	}

	// Cell display: primitives as-is, empty/objects as an em dash so a JSON blob never dumps into a cell.
	_formatCell(pValue)
	{
		if ((pValue === null) || (typeof pValue === 'undefined') || (pValue === '')) { return '—'; }
		if (typeof pValue === 'object') { return '—'; }
		return String(pValue);
	}

	// Value for one configured column. A column names a single field (`Key`) or several (`Keys`) — the
	// latter joins the present values with a space (e.g. NameFirst + NameLast → a person's full name, the way
	// the record cards title a User), skipping blanks so an absent first or last name leaves no stray gap.
	_cellValue(pRecord, pColumn)
	{
		if (pColumn && Array.isArray(pColumn.Keys))
		{
			const tmpParts = pColumn.Keys
				.map((pKey) => pRecord[pKey])
				.filter((pValue) => (pValue !== null) && (typeof pValue !== 'undefined') && (String(pValue).trim() !== ''))
				.map((pValue) => String(pValue).trim());
			return tmpParts.length ? tmpParts.join(pColumn.Separator || ' ') : '—';
		}
		return this._formatCell(pRecord[pColumn.Key]);
	}

	_escapeHTML(pValue)
	{
		return String(pValue || '').replace(/[&<>"]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[pChar]);
	}

	get columns()
	{
		return (Array.isArray(this.options.Columns) && (this.options.Columns.length > 0))
			? this.options.Columns
			: [ { Key: this.displayField, DisplayName: this.displayField } ];
	}

	// --- Server queries (base FK filter + optional LK on the display field) ---

	_listFilter(pWithSort)
	{
		let tmpFilter = `FBV~${ this.options.FilterField }~EQ~${ this._parentID() }`;
		if (this._filterTerm) { tmpFilter += `~FBV~${ this.displayField }~LK~${ encodeURIComponent(`%${ this._filterTerm }%`) }`; }
		if (pWithSort) { tmpFilter += `~FSF~${ this.displayField }~ASC~0`; }
		return tmpFilter;
	}

	async _fetchListPage()
	{
		const tmpChild = this.options.ChildRecordSet;
		try
		{
			const tmpCount = await this._getJSON(`${ tmpChild }s/Count/FilteredTo/${ this._listFilter(false) }`);
			const tmpTotal = (tmpCount && (typeof tmpCount.Count !== 'undefined')) ? Number(tmpCount.Count) : 0;
			const tmpOffset = (this._page || 0) * PAGE_SIZE;
			const tmpRecords = await this._getJSON(`${ tmpChild }s/FilteredTo/${ this._listFilter(true) }/${ tmpOffset }/${ PAGE_SIZE }`);
			return { records: Array.isArray(tmpRecords) ? tmpRecords : [], total: tmpTotal, failed: false };
		}
		catch (pError)
		{
			this.pict.log.warn(`ChildListTab (${ this.options.ParentRecordSet } → ${ tmpChild }): list fetch failed`, { Error: pError && (pError.message || pError) });
			return { records: [], total: 0, failed: true };
		}
	}

	// --- Context chips (a Line Item is meaningless without its Contract / Project) ---

	_normalizeChipFields()
	{
		return (Array.isArray(this.options.ChipFields) ? this.options.ChipFields : [])
			.map((pChip) => ((typeof pChip === 'string') ? { Field: pChip } : pChip))
			.filter((pChip) => pChip && pChip.Field);
	}

	// Direct fields render as-is; reference chips ({ Field, Entity, DisplayField }) are BATCH-resolved (one
	// INN fetch per referenced entity, per page — not per row). Returns an array parallel to pRecords.
	async _decorateChips(pRecords)
	{
		const tmpChips = this._normalizeChipFields();
		if ((tmpChips.length < 1) || (!Array.isArray(pRecords)) || (pRecords.length < 1)) { return (pRecords || []).map(() => []); }

		const tmpMaps = {};
		for (const pChip of tmpChips)
		{
			if (!pChip.Entity) { continue; }
			const tmpIDs = Array.from(new Set(pRecords.map((pRecord) => pRecord[pChip.Field]).filter((pID) => pID && (pID !== 0) && (pID !== '0'))));
			const tmpMap = {};
			if (tmpIDs.length > 0)
			{
				let tmpRefs = [];
				try { tmpRefs = await this._getJSON(`${ pChip.Entity }s/FilteredTo/FBL~ID${ pChip.Entity }~INN~${ tmpIDs.join(',') }/0/${ PAGE_SIZE * 4 }`); }
				catch (pError) { this.pict.log.warn(`ChildListTab chip resolve failed (${ pChip.Entity })`, { Error: pError && (pError.message || pError) }); }
				(Array.isArray(tmpRefs) ? tmpRefs : []).forEach((pRef) => { tmpMap[pRef[`ID${ pChip.Entity }`]] = pRef[pChip.DisplayField || 'Name']; });
			}
			tmpMaps[pChip.Field] = tmpMap;
		}

		return pRecords.map((pRecord) => tmpChips
			.map((pChip) =>
			{
				const tmpValue = pChip.Entity ? tmpMaps[pChip.Field][pRecord[pChip.Field]] : pRecord[pChip.Field];
				if ((tmpValue === undefined) || (tmpValue === null) || (tmpValue === '')) { return null; }
				return { Text: pChip.Label ? `${ pChip.Label }: ${ tmpValue }` : String(tmpValue) };
			})
			.filter(Boolean));
	}

	// --- Build the rows for a page (with context chips on the display cell) ---

	async _buildRows(pRecords)
	{
		const tmpChild = this.options.ChildRecordSet;
		const tmpColumns = this.columns;
		const tmpRows = pRecords.map((pRecord) =>
		(
			{
				ViewID: this.viewID,
				ID: pRecord[this.childIDField],
				Route: `#/PSRS/${ tmpChild }/View/${ pRecord[this.childGUIDField] || '' }`,
				Cells: tmpColumns.map((pColumn) => ({ Value: this._cellValue(pRecord, pColumn), Chips: [] }))
			}
		));
		// Chips hang on the DISPLAY column's cell (the primary identifier), not blindly Cells[0].
		const tmpDisplayIndex = Math.max(0, tmpColumns.findIndex((pColumn) => pColumn.Key === this.displayField));
		const tmpRowChips = await this._decorateChips(pRecords);
		tmpRows.forEach((pRow, pIndex) =>
		{
			const tmpCell = pRow.Cells[tmpDisplayIndex] || pRow.Cells[0];
			if (tmpCell) { tmpCell.Chips = tmpRowChips[pIndex] || []; }
		});
		return { Rows: tmpRows, Columns: tmpColumns };
	}

	// The paged list region (count + table + pagination) — rendered by the main template and re-rendered by
	// _reloadList on a page/filter change.
	_buildListState(pRows, pColumns, pTotal, pFailed)
	{
		const tmpStart = (pTotal === 0) ? 0 : ((this._page || 0) * PAGE_SIZE) + 1;
		const tmpEnd = Math.min(pTotal, ((this._page || 0) + 1) * PAGE_SIZE);
		const tmpHasRows = (pRows.length > 0);
		return {
			CountLabel: (pTotal > 0) ? `Showing ${ tmpStart }–${ tmpEnd } of ${ pTotal } ${ this.noun }` : '',
			TableSlot: tmpHasRows ? [ { Rows: pRows, Columns: pColumns } ] : [],
			EmptySlot: tmpHasRows ? [] : [ { EmptyMessage: pFailed
				? 'Could not load linked records.'
				: (this._filterTerm ? `No ${ this.noun } match &ldquo;${ this._escapeHTML(this._filterTerm) }&rdquo;.` : `No ${ this.noun } linked yet — search above to link some.`) } ],
			PaginationSlot: (pTotal > PAGE_SIZE) ? [ {
				ViewID: this.viewID,
				RangeLabel: `Page ${ (this._page || 0) + 1 } of ${ Math.max(1, Math.ceil(pTotal / PAGE_SIZE)) }`,
				PrevDisabled: ((this._page || 0) <= 0) ? 'disabled' : '',
				NextDisabled: (tmpEnd >= pTotal) ? 'disabled' : ''
			} ] : []
		};
	}

	async _loadState()
	{
		const tmpParentID = this._parentID();
		const tmpCanEdit = (typeof tmpParentID !== 'undefined') && (tmpParentID !== null) && (tmpParentID !== '');
		const tmpAddSlot = tmpCanEdit
			? [ { ViewID: this.viewID, AddLabel: `Link ${ this.noun }`, SearchPlaceholder: `Search ${ this.options.ChildRecordSet } to link…`, ResultsContainerID: `PCFG-CL-Results-${ this.viewID }` } ]
			: [];

		if (!tmpCanEdit)
		{
			this._currentIDs = new Set();
			this._state = {
				ViewID: this.viewID, AddSlot: [], FilterSlot: [], ListContainerID: `PCFG-CL-List-${ this.viewID }`,
				ListState: { CountLabel: '', TableSlot: [], EmptySlot: [ { EmptyMessage: 'Save the record before linking related records.' } ], PaginationSlot: [] }
			};
			return;
		}

		// Fresh render (initial open, or after a link/unlink) starts at page 0, unfiltered.
		this._page = 0;
		this._filterTerm = '';
		const tmpPage = await this._fetchListPage();
		this._total = tmpPage.total;
		this._currentIDs = new Set(tmpPage.records.map((pRecord) => pRecord[this.childIDField]));
		const tmpBuilt = await this._buildRows(tmpPage.records);

		this._state = {
			ViewID: this.viewID,
			AddSlot: tmpAddSlot,
			// The filter is a server query, so offer it whenever the full set spans more than one page.
			FilterSlot: (tmpPage.total > PAGE_SIZE) ? [ { ViewID: this.viewID, Noun: this.noun } ] : [],
			ListContainerID: `PCFG-CL-List-${ this.viewID }`,
			ListState: this._buildListState(tmpBuilt.Rows, tmpBuilt.Columns, tmpPage.total, tmpPage.failed)
		};
	}

	// Re-fetch the current page + count and re-render ONLY the list region (keeps the filter input focused).
	async _reloadList()
	{
		const tmpPage = await this._fetchListPage();
		this._total = tmpPage.total;
		this._currentIDs = new Set(tmpPage.records.map((pRecord) => pRecord[this.childIDField]));
		const tmpBuilt = await this._buildRows(tmpPage.records);
		const tmpHTML = this.pict.parseTemplateByHash('PCFG-ChildListTab-ListRegion', this._buildListState(tmpBuilt.Rows, tmpBuilt.Columns, tmpPage.total, tmpPage.failed));
		this.pict.ContentAssignment.assignContent(`#PCFG-CL-List-${ this.viewID }`, tmpHTML);
	}

	// The read view's `View` tab type calls renderAsync() with no arguments. Fetch first, then render the
	// per-instance state as the template Record into the tab-body destination the read view stamped on us.
	async renderAsync(pRenderable, pDestinationAddress, pData, fCallback)
	{
		await this._loadState();
		const tmpRenderable = pRenderable || this.options.DefaultRenderable;
		const tmpDestination = pDestinationAddress || this.options.DefaultDestinationAddress;
		return super.renderAsync(tmpRenderable, tmpDestination, pData || this._state, fCallback);
	}

	// --- Server-side filter + pagination over the connected list ---

	filterCurrent(pTerm)
	{
		clearTimeout(this._filterTimer);
		const tmpTerm = (pTerm || '').trim();
		// Debounce so we don't query the server on every keystroke.
		this._filterTimer = setTimeout(() =>
		{
			this._filterTerm = tmpTerm;
			this._page = 0;
			this._reloadList();
		}, 300);
	}

	gotoPage(pDelta)
	{
		const tmpMaxPage = Math.max(0, Math.ceil((this._total || 0) / PAGE_SIZE) - 1);
		const tmpNext = Math.min(tmpMaxPage, Math.max(0, (this._page || 0) + pDelta));
		if (tmpNext === (this._page || 0)) { return; }
		this._page = tmpNext;
		this._reloadList();
	}

	// --- Link / unlink (set or clear the child's foreign key to this parent) ---

	async link(pChildID)
	{
		const tmpParentID = this._parentID();
		if (!tmpParentID || !pChildID) { return; }
		try
		{
			await this._putJSON(this.options.ChildRecordSet, { [this.childIDField]: pChildID, [this.options.FilterField]: tmpParentID });
			this._toast(`Linked.`, 'success');
		}
		catch (pError)
		{
			this._toast(`Could not link: ${ pError && (pError.message || pError) }`, 'error');
		}
		this._searchResults = [];
		await this.renderAsync();
	}

	async unlink(pChildID)
	{
		if (!pChildID) { return; }
		try
		{
			await this._putJSON(this.options.ChildRecordSet, { [this.childIDField]: pChildID, [this.options.FilterField]: 0 });
			this._toast(`Unlinked.`, 'success');
		}
		catch (pError)
		{
			this._toast(`Could not unlink: ${ pError && (pError.message || pError) }`, 'error');
		}
		await this.renderAsync();
	}

	// --- Live candidate search (link more) — server LK on the display field, cull the current page's links ---

	async search(pTerm)
	{
		const tmpTerm = (pTerm || '').trim();
		if (tmpTerm.length < 2)
		{
			this._searchResults = [];
			this._renderResults();
			return;
		}
		const tmpChild = this.options.ChildRecordSet;
		const tmpField = this.displayField;
		let tmpRecords = [];
		try
		{
			tmpRecords = await this._getJSON(`${ tmpChild }s/FilteredTo/FBV~${ tmpField }~LK~${ encodeURIComponent(`%${ tmpTerm }%`) }~FSF~${ tmpField }~ASC~0/0/12`);
		}
		catch (pError)
		{
			this.pict.log.warn(`ChildListTab search failed`, { Error: pError && (pError.message || pError) });
		}
		tmpRecords = Array.isArray(tmpRecords) ? tmpRecords : [];
		const tmpCurrent = this._currentIDs || new Set();
		const tmpFiltered = tmpRecords.filter((pRecord) => !tmpCurrent.has(pRecord[this.childIDField])).slice(0, 10);
		const tmpChips = await this._decorateChips(tmpFiltered);
		this._searchResults = tmpFiltered.map((pRecord, pIndex) => (
			{
				ViewID: this.viewID,
				ID: pRecord[this.childIDField],
				Display: this._formatCell(pRecord[tmpField]) || `#${ pRecord[this.childIDField] }`,
				Chips: tmpChips[pIndex] || []
			}));
		this._renderResults();
	}

	_renderResults()
	{
		const tmpResults = this._searchResults || [];
		const tmpHTML = this.pict.parseTemplateByHash('PCFG-ChildListTab-Results', { Results: tmpResults });
		this.pict.ContentAssignment.assignContent(`#PCFG-CL-Results-${ this.viewID }`, tmpHTML);
	}

	_toast(pMessage, pType)
	{
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && (typeof tmpModal.toast === 'function')) { tmpModal.toast(pMessage, { type: pType || 'info' }); }
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

const _DEFAULT_CONFIGURATION =
{
	ViewIdentifier: 'PCFG-ChildListTab',

	DefaultRenderable: 'PCFG-ChildListTab-Renderable',
	DefaultDestinationAddress: '#PCFG-ChildListTab-Container',
	DefaultTemplateRecordAddress: false,

	AutoInitialize: false,
	AutoRender: false,
	AutoSolveWithApp: false,

	CSS: /*css*/`
		.pcfg-childlist-add { margin: 0 0 1rem; }
		.pcfg-childlist-add label { display: block; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); margin-bottom: 0.3rem; }
		.pcfg-childlist-search { width: 100%; box-sizing: border-box; padding: 0.5rem 0.75rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #1f2733); font: inherit; font-size: 0.92rem; }
		.pcfg-childlist-search:focus { outline: none; border-color: var(--theme-color-brand-primary, #5650e6); }
		.pcfg-childlist-results { margin-top: 0.25rem; }
		.pcfg-childlist-result { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.45rem 0.7rem; border: 1px solid var(--theme-color-border-light, #eef0f4); border-radius: 6px; margin-top: 0.25rem; cursor: pointer; background: var(--theme-color-background-primary, #fff); transition: background 0.12s ease; }
		.pcfg-childlist-result:hover { background: var(--theme-color-background-selected, #e3edfb); border-color: var(--theme-color-brand-primary, #5650e6); }
		.pcfg-childlist-result-add { flex: 0 0 auto; color: var(--theme-color-brand-primary, #5650e6); font-size: 0.8rem; font-weight: 600; white-space: nowrap; }
		.pcfg-childlist-result-main { display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
		.pcfg-childlist-result-name { font-weight: 500; }
		.pcfg-childlist-chips { display: flex; flex-wrap: wrap; gap: 0.3rem; margin-top: 0.25rem; }
		.pcfg-childlist-chips:empty { margin-top: 0; }
		.pcfg-childlist-chip { display: inline-block; font-size: 0.7rem; font-weight: 600; color: var(--theme-color-text-secondary, #45505f); background: var(--theme-color-background-tertiary, #eef0f4); border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 999px; padding: 0.05rem 0.5rem; white-space: nowrap; }
		.pcfg-childlist-count { font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); margin: 0 0 0.6rem; }
		.pcfg-childlist-filter { width: 100%; box-sizing: border-box; padding: 0.4rem 0.7rem; margin: 0 0 0.6rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #1f2733); font: inherit; font-size: 0.88rem; }
		.pcfg-childlist-filter:focus { outline: none; border-color: var(--theme-color-brand-primary, #5650e6); }
		.pcfg-childlist-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; overflow: hidden; }
		.pcfg-childlist-table thead th { text-align: left; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); background: var(--theme-color-background-tertiary, #eef0f4); padding: 0.5rem 0.75rem; border-bottom: 1px solid var(--theme-color-border-default, #d7dce3); }
		.pcfg-childlist-table tbody td { padding: 0.5rem 0.75rem; border-bottom: 1px solid var(--theme-color-border-light, #eef0f4); font-size: 0.92rem; color: var(--theme-color-text-primary, #1f2733); }
		.pcfg-childlist-table tbody tr.pcfg-childlist-row { cursor: pointer; transition: background 0.12s ease; }
		.pcfg-childlist-table tbody tr.pcfg-childlist-row:hover { background: var(--theme-color-background-tertiary, #f5f6f8); }
		.pcfg-childlist-table tbody tr:last-child td { border-bottom: none; }
		.pcfg-childlist-unlink { text-align: right; white-space: nowrap; }
		.pcfg-childlist-unlink button { border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-muted, #6b7686); border-radius: 6px; padding: 0.2rem 0.5rem; font: inherit; font-size: 0.8rem; cursor: pointer; transition: all 0.12s ease; }
		.pcfg-childlist-unlink button:hover { border-color: var(--theme-color-status-error, #c0504d); color: var(--theme-color-status-error, #c0504d); }
		.pcfg-childlist-empty { color: var(--theme-color-text-muted, #6b7686); text-align: center; padding: 1.25rem 0; }
		.pcfg-childlist-pagination { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-top: 0.65rem; }
		.pcfg-childlist-page-label { font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); }
		.pcfg-childlist-page-btn { display: inline-flex; align-items: center; gap: 0.3rem; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-secondary, #45505f); border-radius: 7px; padding: 0.35rem 0.75rem; font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: all 0.12s ease; }
		.pcfg-childlist-page-btn:hover:not([disabled]) { border-color: var(--theme-color-brand-primary, #5650e6); color: var(--theme-color-brand-primary, #5650e6); }
		.pcfg-childlist-page-btn[disabled] { opacity: 0.45; cursor: default; }
	`,
	CSSPriority: 500,

	Templates:
	[
		{
			Hash: 'PCFG-ChildListTab-Template',
			Template: /*html*/`
				<div class="pcfg-childlist">
					{~TS:PCFG-ChildListTab-AddTemplate:Record.AddSlot~}
					{~TS:PCFG-ChildListTab-FilterInput:Record.FilterSlot~}
					<div id="{~D:Record.ListContainerID~}">{~T:PCFG-ChildListTab-ListRegion:Record.ListState~}</div>
				</div>`
		},
		{
			// Rendered inside {~TS:...:Record.AddSlot~}, so Record IS the AddSlot element (ViewID / AddLabel /
			// SearchPlaceholder / ResultsContainerID). Shown only when the record is saved.
			Hash: 'PCFG-ChildListTab-AddTemplate',
			Template: /*html*/`
				<div class="pcfg-childlist-add">
					<label>{~D:Record.AddLabel~}</label>
					<input class="pcfg-childlist-search" type="text" placeholder="{~D:Record.SearchPlaceholder~}" oninput="_Pict.views['{~D:Record.ViewID~}'].search(this.value)" />
					<div class="pcfg-childlist-results" id="{~D:Record.ResultsContainerID~}"></div>
				</div>`
		},
		{
			// Server-side filter over the connected list (Record is the FilterSlot element).
			Hash: 'PCFG-ChildListTab-FilterInput',
			Template: /*html*/`<input class="pcfg-childlist-filter" type="text" placeholder="Filter {~D:Record.Noun~}…" oninput="_Pict.views['{~D:Record.ViewID~}'].filterCurrent(this.value)" />`
		},
		{
			Hash: 'PCFG-ChildListTab-Results',
			Template: /*html*/`{~TS:PCFG-ChildListTab-ResultRow:Record.Results~}`
		},
		{
			Hash: 'PCFG-ChildListTab-ResultRow',
			Template: /*html*/`<div class="pcfg-childlist-result" onclick="_Pict.views['{~D:Record.ViewID~}'].link({~D:Record.ID~})"><span class="pcfg-childlist-result-main"><span class="pcfg-childlist-result-name">{~D:Record.Display~}</span><span class="pcfg-childlist-chips">{~TS:PCFG-ChildListTab-Chip:Record.Chips~}</span></span><span class="pcfg-childlist-result-add">{~I:Plus~} Link</span></div>`
		},
		{
			Hash: 'PCFG-ChildListTab-Chip',
			Template: /*html*/`<span class="pcfg-childlist-chip">{~D:Record.Text~}</span>`
		},
		{
			// The paged list region — Record is the ListState (count + table + pagination). Re-rendered whole
			// into #PCFG-CL-List-<viewID> by _reloadList on a page/filter change.
			Hash: 'PCFG-ChildListTab-ListRegion',
			Template: /*html*/`
				<div class="pcfg-childlist-count">{~D:Record.CountLabel~}</div>
				{~TS:PCFG-ChildListTab-TableTemplate:Record.TableSlot~}
				{~TS:PCFG-ChildListTab-EmptyTemplate:Record.EmptySlot~}
				{~TS:PCFG-ChildListTab-Pagination:Record.PaginationSlot~}`
		},
		{
			// Inside {~TS:...:Record.TableSlot~}, Record IS the TableSlot element carrying Rows + Columns.
			Hash: 'PCFG-ChildListTab-TableTemplate',
			Template: /*html*/`
				<table class="pcfg-childlist-table">
					<thead><tr>{~TS:PCFG-ChildListTab-HeadCell:Record.Columns~}<th></th></tr></thead>
					<tbody>{~TS:PCFG-ChildListTab-RowTemplate:Record.Rows~}</tbody>
				</table>`
		},
		{
			Hash: 'PCFG-ChildListTab-HeadCell',
			Template: /*html*/`<th>{~D:Record.DisplayName~}</th>`
		},
		{
			// Row-level nav (Record is the ROW, which carries Route); the unlink button stops propagation so it
			// doesn't also navigate.
			Hash: 'PCFG-ChildListTab-RowTemplate',
			Template: /*html*/`<tr class="pcfg-childlist-row" onclick="window.location.hash='{~D:Record.Route~}'">{~TS:PCFG-ChildListTab-Cell:Record.Cells~}<td class="pcfg-childlist-unlink"><button title="Unlink" onclick="event.stopPropagation(); _Pict.views['{~D:Record.ViewID~}'].unlink({~D:Record.ID~})">{~I:Close~} Unlink</button></td></tr>`
		},
		{
			// The display column's cell carries Chips (see _buildRows); other cells have none so the {~TS:~}
			// renders nothing there.
			Hash: 'PCFG-ChildListTab-Cell',
			Template: /*html*/`<td><span class="pcfg-childlist-open-cell">{~D:Record.Value~}</span><span class="pcfg-childlist-chips">{~TS:PCFG-ChildListTab-Chip:Record.Chips~}</span></td>`
		},
		{
			Hash: 'PCFG-ChildListTab-EmptyTemplate',
			Template: /*html*/`<p class="pcfg-childlist-empty">{~D:Record.EmptyMessage~}</p>`
		},
		{
			// Record is the PaginationSlot element (ViewID / RangeLabel / Prev/NextDisabled).
			Hash: 'PCFG-ChildListTab-Pagination',
			Template: /*html*/`
				<div class="pcfg-childlist-pagination">
					<button type="button" class="pcfg-childlist-page-btn" {~D:Record.PrevDisabled~} onclick="_Pict.views['{~D:Record.ViewID~}'].gotoPage(-1)">{~I:ChevronLeft~} Prev</button>
					<span class="pcfg-childlist-page-label">{~D:Record.RangeLabel~}</span>
					<button type="button" class="pcfg-childlist-page-btn" {~D:Record.NextDisabled~} onclick="_Pict.views['{~D:Record.ViewID~}'].gotoPage(1)">Next {~I:ChevronRight~}</button>
				</div>`
		}
	],

	Renderables:
	[
		{
			RenderableHash: 'PCFG-ChildListTab-Renderable',
			TemplateHash: 'PCFG-ChildListTab-Template',
			RenderMethod: 'replace'
		}
	]
};

module.exports = ChildListTab;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
